import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { ToastContainer } from 'react-toastify';
import { useStore } from './store';
import { useAccountSummaryQuery } from './hooks/query/useAccountSummaryQuery';
import { Router } from './router/routes';
import { ROUTES_MAP, ROUTES_TO_ALLOW_MOBILE, ROUTES_TO_AVOID_INIT } from './router/constants';
import GoToApp from './flows/goToApp';
import './i18n/i18n';
import { setToSessionStorage } from './services/storage.service';
import { convertEnvVarToBoolean } from './helpers/envVariableBooleanConversion.helper';
import { screenTypeTracker } from './services/screenWidth.service';
import { TokenExpired } from './flows/TokenExpired';
import { initMixPanel } from './services/mixpanel.service';
import UserMessageSwitch from './flows/UserMessageFlow';
import { initFireBase } from './services/firebase.service';
import { prepareRedirect } from './services/redirectAfterLogin.service';
import { initFingerPrint } from './services/fingerPrint.service';
import { USER_MESSAGE_TYPES } from './flows/UserMessageFlow/constants';
import { ACTIVE_FLOW_REDIRECT_MAP, POSSIBLE_ACTIVE_FLOWS } from './helpers/activeFlowRedirectionMap';
import { ToastOptions } from './constants/externalLibraries';
import { LiliDrawer } from './components/LiliDrawer';
import InactiveTimer from './services/inactive.service';
import SideBar from './flows/sidebar';
import SmallViewport from './generic-components/SmallViewport';
import { AccountantAccountSwitcher } from './flows/profile/components/accountant-account-switcher/AccountantAccountSwitcher';
import { ISummaryData } from './types';
import { AccountantAccountSwitcherMode } from './flows/profile/components/accountant-account-switcher/types';
import { useAdaService } from './hooks/services/useAdaService';
import { useGlobalModalsManager } from './services/useGlobalModalsManager';

const App = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { search } = useLocation();
  const webAppDisabled = convertEnvVarToBoolean(process.env.REACT_APP_DISABLE_WEBAPP!); // webAppDisabled is a temporary flag for pre-production phase.
  const hydrateCustomerId = useStore((state) => state.hydrateCustomerId);
  const currentWidth = useStore((state) => state.currentWidth);
  const summaryData: ISummaryData = useStore((state) => state.summary);
  const isAuthorizedUser = summaryData?.authorizedUserData !== undefined;
  const setFingerPrint = useStore((state) => state.setFingerPrint);
  const resetUserSliceId = useStore((state) => state.resetUserSliceId);
  const userMessageObject = useStore((state) => state.messageObject);
  const [isInitiated, setIsInitiated] = useState<boolean>(false);
  const [isHydrationFetch, setIsHydrationFetch] = useState<boolean>(false);
  const navigate = useNavigate();
  const isTokenExpired = useStore((state) => state.isTokenExpired);
  const setTokenStatus = useStore((state) => state.setTokenStatus);
  const setSearchParams = useStore((state) => state.setSearchParams);
  const showAccountantSwitcherModal = useStore((state) => state.showAccountantSwitcherModal);
  const setShowAccountantSwitcherModal = useStore((state) => state.setShowAccountantSwitcherModal);
  const activeDrawerTransaction = useStore((state) => state.activeDrawerTransaction);
  const setActiveDrawerTransaction = useStore((state) => state.setActiveDrawerTransaction);
  const { resetUserMessageSlice } = useStore(state => state);

  const { startAdaChat } = useAdaService();

  const shouldBlockMobile = useMemo(
    () => isMobile && !ROUTES_TO_ALLOW_MOBILE.includes(location.pathname.replace('/', '')),
    [location.pathname],
  );

  const userMessageType = useMemo(() => userMessageObject.type, [userMessageObject]);

  const activeFlowId = useMemo(() => {
    try {
      return decodeURIComponent(searchParams.get('flow_id') || '');
    } catch (e) {
      console.error(e);
      return '';
    }
  }, [searchParams]);

  const handleFallback = () => {
    navigate(ROUTES_MAP.LOGIN, { replace: true });
    setIsInitiated(true);
  };

  const handleUserAutoSwitch = () => {
    if (!searchParams) return;
    const crid = searchParams.get('crid');
    if (crid) {
      /** Update LocalStorage with new customerId */
      setToSessionStorage({ key: 'customerId', value: crid });
      /** Reset logged-in user id */
      resetUserSliceId(crid);
    }
  };

  const { refetch, error } = useAccountSummaryQuery(async (userData) => {
    setTimeout(() => {
      setTokenStatus(false);
      setIsHydrationFetch(false);
    }, 5000);
    if (userData) {
      setIsInitiated(true);
      await startAdaChat();
    } else {
      handleFallback();
    }
  });
  const onInit = async () => {
    let shouldAvoidInit: boolean = false;
    setSearchParams(search);
    initMixPanel();
    await initFireBase();
    try {
      const fp = await initFingerPrint();
      setFingerPrint(fp);
    } catch (e) {
      console.error('error loading fp');
    }

    ROUTES_TO_AVOID_INIT.forEach((route) => {
      if (location.pathname.includes(route)) {
        shouldAvoidInit = true;
      }
    });

    if (shouldAvoidInit) {
      setIsInitiated(true);
      return;
    }

    if (activeFlowId && POSSIBLE_ACTIVE_FLOWS.includes(activeFlowId)) {
      const destination = ACTIVE_FLOW_REDIRECT_MAP[activeFlowId as keyof typeof ACTIVE_FLOW_REDIRECT_MAP];
      prepareRedirect(destination);
    }
    if (!isAuthorizedUser) {
      handleUserAutoSwitch();
    }
    const id = await hydrateCustomerId();
    if (id) {
      setIsHydrationFetch(true);
    } else if (id === '') {
      handleFallback();
    }
  };

  const initI18n = async () => {
    // const isCached = getFromLocalStorage('lang');
    // if (isCached) {
    //   await i18next.changeLanguage(isCached);
    // } else {
    //   const userLang = navigator.language.toString().substring(0, 2);
    //   await i18next.changeLanguage(userLang);
    // }
    onInit();
  };

  useEffect(() => {
    if (!isHydrationFetch) return;
    refetch();
  }, [isHydrationFetch]);

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setTokenStatus(false);
        setIsHydrationFetch(false);
      }, 5000);
      handleFallback();
    }
  }, [error]);

  useEffect(() => {
    screenTypeTracker();
    initI18n();
    prepareRedirect(location.pathname);
    setActiveDrawerTransaction(null);
  }, []);

  const isUserLoggedIn = useMemo(() => !!summaryData?.customer?.externalId, [summaryData]);

  const { triggerModalLogic } = useGlobalModalsManager();
  useEffect(() => {
    if (isUserLoggedIn) {
      triggerModalLogic();
    } else {
      resetUserMessageSlice();
    }
  }, [isUserLoggedIn]);

  return (
    <div>
      {((shouldBlockMobile || webAppDisabled) && <GoToApp />) ||
        (isInitiated && (
          <div className='h-screen min-h-[835px] w-full overflow-x-auto relative' id='webapp'>
            {isTokenExpired && !isHydrationFetch && <TokenExpired />}
            {userMessageType && userMessageType !== USER_MESSAGE_TYPES.ZERO_STATE && (
              <UserMessageSwitch userMessageObject={userMessageObject} />
            )}
            {isUserLoggedIn && showAccountantSwitcherModal && (
              <AccountantAccountSwitcher
                mode={AccountantAccountSwitcherMode.ONBOARDING}
                onClose={() => {
                  setShowAccountantSwitcherModal(false);
                }}
              />
            )}
            <div className='flex items-start h-full'>
              {isUserLoggedIn && <InactiveTimer />}
              {isUserLoggedIn && <SideBar isResponsiveMenu={false} />}
              <SmallViewport currentWidth={currentWidth} />
              <Router />
            </div>

            <ToastContainer className='z-[999999999]' {...ToastOptions} />
            {!!activeDrawerTransaction && <LiliDrawer transaction={activeDrawerTransaction} />}
          </div>
        ))}
    </div>
  );
};

export default App;
